/**
 * useAdminNotifications — central operacional admin
 *
 * Polling leve (25s) sobre módulos críticos. Compara contadores entre ciclos;
 * se algum aumentar, toca um sinal sonoro discreto (Web Audio API, sem arquivo
 * externo) e exibe um toast leve.
 *
 * Escopo blindado: sem alterações de backend, autenticação, regras de negócio,
 * dashboards ou rotas. Usa apenas endpoints admin já existentes.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';

const POLL_INTERVAL_MS = 25000; // 25s — leve e não invasivo

// Categorias monitoradas e prioridade (define se toca som ao aumentar)
//   high   → som obrigatório
//   medium → som leve
//   low    → apenas badge
const CATEGORY_DEFS = {
  fraud:           { label: 'Antifraude',     toastMsg: 'Novo alerta antifraude',   priority: 'high'   },
  disputes:        { label: 'Contestações',   toastMsg: 'Nova contestação',         priority: 'high'   },
  paymentDisputes: { label: 'Disputas Pagto', toastMsg: 'Nova disputa de pagamento', priority: 'high'   },
  pendingPayments: { label: 'Pgtos Pendentes',toastMsg: 'Novo pagamento aguardando confirmação', priority: 'high' },
  support:         { label: 'Suporte',        toastMsg: 'Novo ticket de suporte',   priority: 'medium' },
  expiringSubs:    { label: 'Assinaturas',    toastMsg: 'Assinatura vence em breve', priority: 'medium'},
  unverified:      { label: 'E-mail pendente',toastMsg: 'Novo cliente cadastrado',  priority: 'low'    },
};

// Gera um beep curto e elegante via Web Audio API (sem dependência externa).
let __audioCtx = null;
const playNotificationTone = (variant = 'standard') => {
  try {
    if (!__audioCtx) {
      // eslint-disable-next-line no-undef
      __audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = __audioCtx;
    // Resume context if browser blocked autoplay
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;
    const notes = variant === 'high' ? [880, 660] : [660, 990];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.13);
      // Envelope discreto (Slack-like)
      gain.gain.setValueAtTime(0.0001, now + i * 0.13);
      gain.gain.exponentialRampToValueAtTime(0.12, now + i * 0.13 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.13 + 0.13);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.13);
      osc.stop(now + i * 0.13 + 0.14);
    });
  } catch (_e) {
    // Falha silenciosa — som é UX-only, não pode quebrar nada
  }
};

const extractDashboardCounts = (dashboardData) => {
  const out = {
    unverified: 0,
    pendingPayments: 0,
    support: 0,
    fraud: 0,
    expiringSubs: 0,
  };
  const alerts = dashboardData?.alerts || [];
  alerts.forEach((a) => {
    // O backend serializa o número no início do title: "{N} ... descrição"
    const match = (a?.title || '').match(/^(\d+)\b/);
    const n = match ? parseInt(match[1], 10) : 0;
    switch (a?.icon) {
      case 'mail':           out.unverified = n; break;
      case 'credit-card':    out.pendingPayments = n; break;
      case 'headphones':     out.support = n; break;
      case 'alert-triangle': out.fraud = n; break;
      case 'clock':          out.expiringSubs = n; break;
      default: break;
    }
  });
  return out;
};

export default function useAdminNotifications() {
  const { user } = useContext(AuthContext);
  const [counts, setCounts] = useState({
    unverified: 0,
    pendingPayments: 0,
    support: 0,
    fraud: 0,
    expiringSubs: 0,
    disputes: 0,
    paymentDisputes: 0,
  });
  const lastCountsRef = useRef(null); // null = baseline ainda não definido
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') return undefined;

    let cancelled = false;

    const fetchAll = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const [dashRes, disputesRes, payDisputesRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard-v2`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${API}/disputes/admin/list?status=aberta`, { headers }).catch(() => ({ data: { disputes: [] } })),
          axios.get(`${API}/admin/payment-disputes?status=open`, { headers }).catch(() => ({ data: { disputes: [] } })),
        ]);

        if (cancelled) return;

        const dashCounts = extractDashboardCounts(dashRes.data);
        const next = {
          ...dashCounts,
          disputes: (disputesRes.data?.disputes || []).length,
          paymentDisputes: (payDisputesRes.data?.disputes || []).length,
        };

        // Primeira execução → apenas baseline, NÃO toca som
        if (lastCountsRef.current === null) {
          lastCountsRef.current = next;
          setCounts(next);
          return;
        }

        // Detectar aumentos por categoria
        let highIncreased = false;
        let mediumIncreased = false;
        let triggeredMsg = null;
        Object.keys(CATEGORY_DEFS).forEach((key) => {
          const before = lastCountsRef.current[key] || 0;
          const after = next[key] || 0;
          if (after > before) {
            const def = CATEGORY_DEFS[key];
            if (def.priority === 'high') highIncreased = true;
            else if (def.priority === 'medium') mediumIncreased = true;
            if (!triggeredMsg) triggeredMsg = def.toastMsg;
          }
        });

        if (highIncreased || mediumIncreased) {
          playNotificationTone(highIncreased ? 'high' : 'standard');
          if (triggeredMsg) {
            toast(triggeredMsg, { duration: 4000 });
          }
        }

        lastCountsRef.current = next;
        setCounts(next);
      } catch (_e) {
        // silencioso — não atrapalha o admin
      }
    };

    // Primeira chamada imediata para baseline
    fetchAll();
    // Polling leve
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user]);

  return counts;
}
